import React from 'react';

import { useNavigate } from 'react-router';

import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import ConfirmationModal from '@/components/popups/ConfirmationModal';

import { routes } from 'constants/index';

import {
  trainTogetherModeAtom,
  trainTogetherModeDispatchAtom,
} from '@/atoms/trainTogetherMode';

interface ConfirmationModalsProps {
  isOpen: boolean;
}

const trainTogetherModePickAtom = focusAtom(trainTogetherModeAtom, o =>
  o.pick(['showProgressModal', 'trainTogetherModeData']),
);

const setIsBeforeNavigateAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('isBeforeNavigate'),
);

const ConfirmationModals: React.FC<ConfirmationModalsProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const setIsBeforeNavigate = useSetAtom(setIsBeforeNavigateAtom);
  const sendTrainTogetherModeAction = useSetAtom(trainTogetherModeDispatchAtom);
  const {
    showProgressModal,
    trainTogetherModeData: { planId, planName },
  } = useAtomValue(trainTogetherModePickAtom);

  const onExit = () => {
    // This one is only the Train Together Modal
    sendTrainTogetherModeAction('hideProgressModal');
    setIsBeforeNavigate(true);
    sendTrainTogetherModeAction('exitTrainTogetherMode');
    sendTrainTogetherModeAction('resetTrainTogetherModeData');

    if (window.native.isNative) {
      sendNativeMessage({ type: 'trainTogetherModeActive', payload: false });
      sendNativeMessage({ type: 'navigateTo', payload: '/' });
      return;
    }

    navigate({
      pathname: routes.plans.managePlan({ planId }),
    });
  };

  const goToExplorePage = () => {
    setIsBeforeNavigate(true);
    sendTrainTogetherModeAction('exitTrainTogetherMode');
    sendTrainTogetherModeAction('resetTrainTogetherModeData');

    if (window.native.isNative) {
      sendNativeMessage({ type: 'trainTogetherModeActive', payload: false });
      sendNativeMessage({ type: 'navigateTo', payload: '/' });
      return;
    }

    navigate(
      {
        pathname: '/',
      },
      {
        state: { fromTrainTogetherMode: true },
      },
    );
  };

  return (
    <>
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.exitModalBody')}
        headerText={t('TrainingPlans.trainTogether.exitModalHeader')}
        isOpen={showProgressModal}
        onClose={() => sendTrainTogetherModeAction('hideProgressModal')}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onExit}
        primaryButtonText={t('Button.exit')}
        secondaryButtonHandler={() =>
          sendTrainTogetherModeAction('hideProgressModal')
        }
        secondaryButtonText={t('Button.stay')}
      />
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.completedPlanModal', {
          planName: planName,
        })}
        headerText={t('Generic.planCompleted')}
        isOpen={isOpen}
        onClose={goToExplorePage}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={goToExplorePage}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};

export default ConfirmationModals;
